import {
  Switch,
    Form,
    Row,
    Col,
    Input,
    Space,
    Button,
    message,
    InputNumber,
    Select,
    Modal,
    Card,
    Typography,
    Divider,
    Tabs
} from "antd";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
    ExclamationCircleOutlined,
    LeftOutlined,
    SaveOutlined,
    DeleteOutlined,
    InfoCircleOutlined,
    PlusOutlined,
    SettingOutlined,
    EyeOutlined,
    ShoppingOutlined,
    SyncOutlined
} from "@ant-design/icons";
import CustomUpload from "../customUpload";
import axiosInstance from "../../utils/axios";
import CustomSunEditor from "../customSunEditor";
import SeoScore from "../SeoScore";

const slugify = (text) => {
    if (!text) return "";
    return text
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .replace(/[^a-zA-Z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .toLowerCase();
};

const { Title, Text } = Typography;

export default function ProductForm({
    id = "",
    initialValues = {},
    onSubmit = () => { },
    loading = false,
}) {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [categoryChild, setCategoryChild] = useState([]);
    const [attribute, setAttribute] = useState([]);
    const [selectAttribute, setSelectAttribute] = useState([]);
    const [seoKeyword, setSeoKeyword] = useState("");
    const [formValues, setFormValues] = useState({});

    // Editor initial values (state) - chỉ dùng để load lần đầu vào CustomSunEditor
    const [editorValue, setEditorValue] = useState('');
    const [editorInfor, setEditorInfor] = useState('');
    const [editorSpec, setEditorSpec] = useState('');

    // Editor current values (ref) - luôn có giá trị mới nhất, không gây re-render
    const editorValueRef = useRef('');
    const editorInforRef = useRef('');
    const editorSpecRef = useRef('');

    // SunEditor instances - dùng để gọi getContents() trực tiếp khi submit
    const descEditorInstance = useRef(null);
    const inforEditorInstance = useRef(null);
    const specEditorInstance = useRef(null);

    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 100,
    });
    const [paginationAttribute, setPaginationAttribute] = useState({
        page: 1,
        pageSize: 100,
    });

    const fetchCategoryChild = async () => {
        await axiosInstance.get(`/category-child/getAll`, { params: pagination })
            .then((res) => {
                const data = res?.data;
                setCategoryChild(data);
            })
            .catch(() => message.error("Error server!"));
    };

    const fetchAttribute = async () => {
        await axiosInstance.get(`/attribute/getAll`, { params: paginationAttribute })
            .then((res) => {
                const data = res?.data;
                setAttribute(data);
            })
            .catch(() => message.error("Error server!"));
    };

    const handleChange = (selectedIds, index) => {
        const selectedOptions = attribute?.data
            .filter(item => selectedIds.includes(item.attribute_id))
            .map(({ attribute_id, type }) => ({ id: attribute_id, type }));

        setSelectAttribute(prev => {
            const newAttributes = [...prev];
            newAttributes[index] = selectedOptions;
            return newAttributes;
        });
    };

    const deleteProduct = async () => {
        await axiosInstance.delete(`/product/delete/${id}`)
            .then((res) => message.success(String(res?.data?.message)))
    };

    const removeProduct = async () => {
        try {
            await deleteProduct();
            return navigate("/admin/quan-ly-san-pham");
        } catch (err) {
            console.log(err.message);
        }
    };

    const confirmDeleteBusiness = () => {
        Modal.confirm({
            icon: <ExclamationCircleOutlined className="text-red-500" />,
            content: "Bạn có chắc chắn xoá sản phẩm này?",
            okText: "Xác nhận",
            okType: 'danger',
            cancelText: "Huỷ",
            onOk: () => removeProduct(),
        });
    };

    useEffect(() => {
        fetchCategoryChild();
        fetchAttribute();
        if (Object.keys(initialValues)?.length > 0) {
            setSelectAttribute(initialValues[0]?.attribute || []);

            const desc = initialValues[0]?.product_description || '';
            const info = initialValues[0]?.product_information || '';
            const spec = initialValues[0]?.product_special || '';

            // Set state (để truyền vào CustomSunEditor lần đầu)
            setEditorValue(desc);
            setEditorInfor(info);
            setEditorSpec(spec);

            // Set ref (để dùng khi submit)
            editorValueRef.current = desc;
            editorInforRef.current = info;
            editorSpecRef.current = spec;

            setFormValues(initialValues[0]);

            form.resetFields();
            // Sync lại các editor field vào form sau resetFields
            // vì CustomSunEditor không được Ant Design Form tự động nhận diện
            form.setFieldsValue({
                product_description: desc,
                product_information: info,
                product_special: spec,
            });
        }
    }, [form, initialValues]);


    const [debounceTimer, setDebounceTimer] = useState(null);
    const isEditingSlug = useRef(false);

    const handleFormChange = (changedValues, allValues) => {
        const currentChangedValues = changedValues || {};
        const currentAllValues = allValues || form.getFieldsValue();

        // Auto-generate slug if product_name changed and slug hasn't been manually touched
        if (currentChangedValues.product_name && (!currentAllValues.product_slug || !isEditingSlug.current)) {
            const newSlug = slugify(currentChangedValues.product_name);
            form.setFieldsValue({ product_slug: newSlug });
        }

        // Mark slug as manually edited if user changes it
        if (currentChangedValues.product_slug !== undefined) {
            isEditingSlug.current = true;
        }

        if (debounceTimer) clearTimeout(debounceTimer);
        const timer = setTimeout(() => {
            setFormValues(currentAllValues);
        }, 500);
        setDebounceTimer(timer);
    };

    const generateSlugManually = () => {
        const name = form.getFieldValue("product_name");
        if (name) {
            const newSlug = slugify(name);
            form.setFieldsValue({ product_slug: newSlug });
            message.success("Đã tạo đường dẫn mới từ tên sản phẩm!");
        } else {
            message.warning("Vui lòng nhập tên sản phẩm trước khi tạo đường dẫn!");
        }
    };

    const onFinishFailed = (errorInfo) => {
        console.log('Failed:', errorInfo);
        const errorFields = errorInfo.errorFields.map(field => field.errors[0]).join(", ");
        message.error(`Vui lòng kiểm tra lại: ${errorFields}`);
    };

    return (
        <div className="bg-gray-50 min-h-screen p-4 md:p-8">
            <Form
                layout={"vertical"}
                form={form}
                initialValues={{ product_status: 'public', ...initialValues?.[0] }}
                onFinish={(data) => {
                    // Lấy nội dung trực tiếp từ editor instance (getContents)
                    const desc = descEditorInstance.current?.getContents() || editorValueRef.current;
                    const info = inforEditorInstance.current?.getContents() || editorInforRef.current;
                    const spec = specEditorInstance.current?.getContents() || editorSpecRef.current;
                    onSubmit({
                        ...data,
                        product_description: desc,
                        product_information: info,
                        product_special: spec,
                    });
                }}
                onFinishFailed={onFinishFailed}
                onValuesChange={handleFormChange}
                scrollToFirstError
            >
                {/* Header Bar */}
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                    <div className="flex items-center gap-4">
                        <Button
                            shape="circle"
                            icon={<LeftOutlined />}
                            onClick={() => navigate("/admin/quan-ly-san-pham")}
                            className="shadow-sm"
                        />
                        <div>
                            <Title level={4} style={{ margin: 0, fontWeight: 800 }}>
                                {id && id !== "tao-moi" ? "Chỉnh sửa sản phẩm" : "Thêm sản phẩm mới"}
                            </Title>
                            <Text type="secondary" className="text-xs text-gray-400">Thiết lập thông tin hiển thị và SEO cho sản phẩm</Text>
                        </div>
                    </div>

                    <Space size="middle">
                        {id && id !== "tao-moi" && (
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={confirmDeleteBusiness}
                            >
                                Xoá sản phẩm
                            </Button>
                        )}
                        <Button
                            type="primary"
                            size="large"
                            htmlType="submit"
                            icon={<SaveOutlined />}
                            loading={loading}
                            className="rounded-xl px-8 font-bold h-12 shadow-blue-200 shadow-lg"
                        >
                            {id && id !== "tao-moi" ? "Cập nhật dữ liệu" : "Đăng bán sản phẩm"}
                        </Button>
                    </Space>
                </div>

                <Row gutter={[24, 24]}>
                    {/* Main Information Grid (Left) */}
                    <Col xs={24} lg={16}>
                        <Space direction="vertical" size="large" className="w-full">
                            <Card
                                title={<div className="flex items-center gap-2"><ShoppingOutlined className="text-blue-600" /><span>Thông tin cơ bản</span></div>}
                                className="rounded-2xl border-none shadow-sm overflow-hidden"
                            >
                                <Form.Item
                                    label={<span className="font-bold text-gray-700">Tên sản phẩm</span>}
                                    name="product_name"
                                    rules={[{ required: true, message: "Tên sản phẩm là bắt buộc!" }]}
                                >
                                    <Input size="large" placeholder="Nhập tên sản phẩm..." className="rounded-lg h-12 border-gray-200" maxLength={255} />
                                </Form.Item>

                                <Form.Item
                                    label={<span className="font-bold text-gray-700">Đường dẫn sản phẩm (Slug)</span>}
                                    name="product_slug"
                                    rules={[{ required: true, message: "Đường dẫn (slug) là bắt buộc!" }]}
                                    className="bg-gray-50 p-4 rounded-xl border border-dashed border-gray-200"
                                >
                                    <Input
                                        size="large"
                                        placeholder="tu-dong-theo-ten-san-pham"
                                        className="rounded-lg h-10 border-gray-300 font-mono text-blue-600"
                                        addonBefore={window.location.origin + "/san-pham/"}
                                        addonAfter={
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<SyncOutlined className="text-blue-500" />}
                                                onClick={generateSlugManually}
                                                className="flex items-center gap-1 hover:text-blue-700"
                                            >
                                                Tạo tự động
                                            </Button>
                                        }
                                    />
                                </Form.Item>

                                <Row gutter={24}>
                                    <Col span={24}>
                                        <Form.Item
                                            label={<span className="font-bold text-gray-700">Giá bán (VNĐ)</span>}
                                            name="product_price"
                                            rules={[{ required: true, message: "Vui lòng nhập giá bán!" }]}
                                        >
                                            <InputNumber
                                                className="w-full rounded-lg h-12"
                                                style={{ width: '100%', height: 48 }}
                                                formatter={value => (value !== undefined && value !== null && value !== '' ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '')}
                                                parser={value => (value ? value.replace(/[^0-9]/g, '') : '')}
                                                size="large"
                                                min={0}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={24}>
                                        <Form.Item
                                            label={<span className="font-bold text-gray-700">Giá gốc/thị trường (VNĐ)</span>}
                                            name="price_mkt"
                                        >
                                            <InputNumber
                                                className="w-full rounded-lg h-12"
                                                style={{ width: '100%', height: 48 }}
                                                formatter={value => (value !== undefined && value !== null && value !== '' ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '')}
                                                parser={value => (value ? value.replace(/[^0-9]/g, '') : '')}
                                                size="large"
                                                min={0}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={24}>
                                        <Form.Item
                                            label={<span className="font-bold text-gray-700">Giá khuyến mãi (%)</span>}
                                            name="product_discount"
                                        >
                                            <InputNumber
                                                min={0} max={100}
                                                className="w-full rounded-lg h-12"
                                                style={{ width: '100%', height: 48 }}
                                                size="large"
                                                formatter={(value) => (value !== undefined && value !== null && value !== '' ? `${value}%` : '')}
                                                parser={(value) => (value ? value.replace(/%/g, '') : '')}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Form.Item
                                    label={<span className="font-bold text-gray-700">Link tiếp thị liên kết (Affiliate)</span>}
                                    name="product_link"
                                    rules={[{ required: true, message: "Vui lòng nhập link tiếp thị!" }]}
                                >
                                    <Input size="large" placeholder="https://..." className="rounded-lg h-12" type="url" />
                                </Form.Item>
                            </Card>

                            {/* Media Gallery Moved Here */}
                            <Card title={<div className="flex items-center gap-2"><SettingOutlined className="text-orange-500" /><span>Thư viện Hình ảnh</span></div>} className="rounded-2xl border-none shadow-sm overflow-hidden">
                                <Row gutter={24}>
                                    <Col xs={24} md={8}>
                                        <Form.Item name="product_image" label={<span className="font-bold text-gray-700">Ảnh đại diện chính</span>} rules={[{ required: true, message: "Bắt buộc chọn ảnh đại diện!" }]}>
                                            <CustomUpload type="image" accept=".png, .jpg, .jpeg, .jfif" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={16}>
                                        <Form.Item name="product_image_slide" label={<span className="font-bold text-gray-700">Ảnh Slide chi tiết (Album)</span>} rules={[{ required: true, message: "Bắt buộc chọn ảnh slide album!" }]}>
                                            <CustomUpload type="image" accept=".png, .jpg, .jpeg, .jfif" multiple />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>

                            {/* Attributes Section Moved Here */}
                            <Card
                                title={<div className="flex items-center justify-between w-full">
                                    <div className="flex items-center gap-2"><SettingOutlined className="text-purple-600" /><span>Thông số sản phẩm</span></div>
                                    <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setSelectAttribute([...selectAttribute, []])} className="rounded-full">Thêm thông số</Button>
                                </div>}
                                className="rounded-2xl border-none shadow-sm overflow-hidden"
                            >
                                <Row gutter={[16, 16]}>
                                    {selectAttribute.map((attrs, attrIndex) => (
                                        <Col xs={24} key={attrIndex}>
                                            <div className="bg-gray-50 p-4 rounded-xl relative border border-gray-100 group">
                                                <Row gutter={16} align="middle">
                                                    <Col flex="auto">
                                                        <Form.Item noStyle name={`attribute_${attrIndex}`}>
                                                            <Select
                                                                mode="multiple"
                                                                showSearch
                                                                placeholder="Tìm thuộc tính..."
                                                                optionFilterProp="label"
                                                                filterOption={(input, option) =>
                                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                                }
                                                                className="w-full"
                                                                onChange={(e) => handleChange(e, attrIndex)}
                                                                options={attribute?.data?.map((value) => ({
                                                                    value: value.attribute_id,
                                                                    label: value.type,
                                                                }))}
                                                            />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col flex="none">
                                                        <Button
                                                            danger
                                                            type="text"
                                                            icon={<DeleteOutlined />}
                                                            onClick={() => setSelectAttribute(selectAttribute.filter((_, i) => i !== attrIndex))}
                                                        />
                                                    </Col>
                                                </Row>

                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                                                    {(selectAttribute[attrIndex] || [])?.map((item, i) => (
                                                        <div key={item.id} className="flex items-center gap-2 bg-white p-2 rounded-lg border border-gray-100 shadow-sm">
                                                            <span className="font-semibold text-gray-500 text-[11px] whitespace-nowrap">{item?.type}:</span>
                                                            <Form.Item
                                                                noStyle
                                                                name={`attribute_${attrIndex}_id_${item.id}`}
                                                                rules={[{ required: true, message: `Bắt buộc nhập giá trị cho ${item?.type}!` }]}
                                                            >
                                                                <Input variant="borderless" placeholder="Giá trị..." className="p-0 text-xs" />
                                                            </Form.Item>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </Card>

                            {/* Editors Moved Here */}
                            <Card className="rounded-2xl border-none shadow-sm overflow-hidden p-0">
                                <Tabs defaultActiveKey="1" className="custom-tabs" tabBarStyle={{ padding: '0 24px', margin: 0 }}>
                                    <Tabs.TabPane forceRender={true} tab={<span className="py-2 px-2 block font-bold">Mô tả sản phẩm</span>} key="1">
                                        <div className="p-4 bg-white min-h-[400px]">
                                            <Form.Item name="product_description" rules={[{ required: true, message: "Vui lòng nhập mô tả sản phẩm!" }, { max: 1000000, message: "Mô tả quá dài (tối đa 1MB nội dung)!" }]}>
                                                <CustomSunEditor
                                                    value={editorValue}
                                                    onGetInstance={(instance) => { descEditorInstance.current = instance; }}
                                                    onChange={(e) => {
                                                        editorValueRef.current = e;
                                                        handleFormChange();
                                                    }}
                                                />
                                            </Form.Item>
                                        </div>
                                    </Tabs.TabPane>
                                    <Tabs.TabPane forceRender={true} tab={<span className="py-2 px-2 block font-bold">Thông tin kỹ thuật</span>} key="2">
                                        <div className="p-4 bg-white">
                                            <Form.Item name="product_information" rules={[{ max: 1000000, message: "Thông tin kỹ thuật quá dài!" }]}>
                                                <CustomSunEditor
                                                    value={editorInfor}
                                                    onGetInstance={(instance) => { inforEditorInstance.current = instance; }}
                                                    onChange={(e) => {
                                                        editorInforRef.current = e;
                                                        handleFormChange();
                                                    }}
                                                />
                                            </Form.Item>
                                        </div>
                                    </Tabs.TabPane>
                                    <Tabs.TabPane forceRender={true} tab={<span className="py-2 px-2 block font-bold">Đặc điểm nổi bật</span>} key="3">
                                        <div className="p-4 bg-white">
                                            <Form.Item name="product_special" rules={[{ max: 1000000, message: "Đặc điểm nổi bật quá dài!" }]}>
                                                <CustomSunEditor
                                                    value={editorSpec}
                                                    onGetInstance={(instance) => { specEditorInstance.current = instance; }}
                                                    onChange={(e) => {
                                                        editorSpecRef.current = e;
                                                        handleFormChange();
                                                    }}
                                                />
                                            </Form.Item>
                                        </div>
                                    </Tabs.TabPane>
                                </Tabs>
                            </Card>
                        </Space>
                    </Col>

                    {/* Sidebar (Right) */}
                    <Col xs={24} lg={8}>
                        <Space direction="vertical" size="large" className="w-full sticky top-4">
                            <Card title={<div className="flex items-center gap-2"><InfoCircleOutlined className="text-green-600" /><span>Cài đặt chung</span></div>} className="rounded-2xl border-none shadow-sm overflow-hidden">
                                <Form.Item
                                    label={<span className="font-bold text-gray-700 text-xs">Trạng thái bán hàng</span>}
                                    name="product_status"
                                    rules={[{ required: true, message: "Vui lòng chọn trạng thái!" }]}
                                >
                                    <Select
                                        size="large"
                                        className="w-full"
                                        options={[
                                            { value: 'public', label: 'Công khai' },
                                            { value: 'draft', label: 'Bản nháp' },
                                        ]}
                                    />
                                </Form.Item>

      <Form.Item
        label="Sách dịch trên ứng dụng"
        name="is_translated_book"
        valuePropName="checked"
        initialValue={false}
      >
        <Switch
          checkedChildren="Bật"
          unCheckedChildren="Tắt"
        />
      </Form.Item>

                                <Form.Item
                                    label={<span className="font-bold text-gray-700 text-xs">Danh mục con</span>}
                                    name="category_child_id"
                                    rules={[{ required: true, message: "Vui lòng chọn danh mục con!" }]}
                                >
                                    <Select
                                        showSearch
                                        size="large"
                                        className="w-full"
                                        placeholder="Tìm danh mục con..."
                                        optionFilterProp="label"
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        options={categoryChild?.data?.map((value) => ({
                                            value: value.category_child_id,
                                            label: value.category_child_name,
                                        }))}
                                    />
                                </Form.Item>
                                <Form.Item label={<span className="font-bold text-gray-700 text-xs">Link PDF/HDSD</span>} name="product_image_file">
                                    <Input placeholder="Gắn link file..." className="rounded-lg h-10" />
                                </Form.Item>
                            </Card>

                            <SeoScore
                                title={formValues.product_name}
                                content={editorValue}
                                slug={formValues.product_slug}
                                keyword={seoKeyword}
                                titleSeo={formValues.product_title_seo}
                                descriptionSeo={formValues.product_description_seo}
                            />

                            <Card
                                title={<div className="flex items-center gap-2"><EyeOutlined className="text-indigo-600" /><span>Tìm kiếm (Google SEO)</span></div>}
                                className="rounded-2xl border-none shadow-sm overflow-hidden"
                            >
                                <Space direction="vertical" className="w-full" size="middle">
                                    <Form.Item
                                        label={<span className="font-bold text-gray-700 text-xs">Thẻ tiêu đề SEO</span>}
                                        name="product_title_seo"
                                        tooltip="Nên dưới 60 ký tự."
                                    >
                                        <Input placeholder="Tự động theo tên..." className="rounded-lg" />
                                    </Form.Item>

                                    <Form.Item
                                        label={<span className="font-bold text-gray-700 text-xs">Thẻ mô tả SEO</span>}
                                        name="product_description_seo"
                                        tooltip="Nên dưới 160 ký tự."
                                    >
                                        <Input.TextArea rows={3} placeholder="Tự động theo mô tả..." className="rounded-lg" />
                                    </Form.Item>

                                    <Form.Item label={<span className="font-bold text-gray-700 text-xs">Từ khóa chính</span>}>
                                        <Input
                                            placeholder="Ví dụ: máy tạo oxy..."
                                            value={seoKeyword}
                                            onChange={(e) => setSeoKeyword(e.target.value)}
                                            className="rounded-lg"
                                        />
                                    </Form.Item>

                                    <Divider className="my-2" />

                                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 italic">
                                        <div className="text-blue-700 font-medium mb-1 text-[11px] flex items-center gap-2">
                                            <img src="https://www.google.com/favicon.ico" className="w-3 h-3" alt="google" />
                                            Google Preview
                                        </div>
                                        <div className="text-blue-800 font-bold mb-1 line-clamp-1 text-sm">
                                            {formValues.product_title_seo || formValues.product_name || "Tên sản phẩm..."}
                                        </div>
                                        <div className="text-green-800 mb-1 text-[10px] flex items-center gap-1">
                                            {window.location.host}/san-pham/{formValues.product_slug || "path"}
                                        </div>
                                        <div className="text-gray-600 text-[11px] leading-relaxed line-clamp-2">
                                            {formValues.product_description_seo || editorValue?.replace(/<[^>]*>/g, '')?.substring(0, 100) || "Mô tả SEO..."}
                                        </div>
                                    </div>
                                </Space>
                            </Card>
                        </Space>
                    </Col>
                </Row>
            </Form>
        </div>
    );
}